import {
  getAddressClientId,
  isAddressProxyEnabled,
  verifyAddressViaProxy,
} from '../../../services/addressApi';
import { SMARTY_EMBEDDED_KEY } from './labConstants';

export const REQUIRED_PATIENT_ADDRESS_FIELDS = ['street', 'city', 'state', 'zip'];

export function isPatientAddressComplete(address = {}) {
  return REQUIRED_PATIENT_ADDRESS_FIELDS.every(field => String(address[field] || '').trim());
}

export function buildPatientAddress(address = {}, zip = '') {
  return {
    street: String(address.street || '').trim(),
    apt: String(address.apt || '').trim(),
    city: String(address.city || '').trim(),
    state: String(address.state || '').trim().toUpperCase(),
    zip: String(zip || address.zip || '').trim(),
  };
}

let streetApiClient = null;

export async function getSmartyStreetApiClient(key = SMARTY_EMBEDDED_KEY) {
  if (streetApiClient) return streetApiClient;
  if (!key) return null;
  try {
    const SmartySDK = await import('smartystreets-javascript-sdk');
    const credentials = new SmartySDK.core.SharedCredentials(key);
    streetApiClient = new SmartySDK.core.ClientBuilder(credentials).buildUsStreetApiClient();
    return streetApiClient;
  } catch (error) {
    console.warn('Could not initialize Smarty Street client:', error);
    return null;
  }
}

/**
 * Verifies a candidate patient address using Smarty Streets US Street API.
 * 
 * @param {Object} candidateAddress - { street, apt, city, state, zip }
 * @param {Object} [options] - Optional overrides (client, key)
 * @returns {Promise<{ isValid: boolean, verified: boolean, normalizedAddress?: Object, error?: string }>}
 */
export async function verifyPatientAddress(candidateAddress = {}, options = {}) {
  const { street, apt = '', city, state, zip } = candidateAddress;

  if (!isPatientAddressComplete(candidateAddress)) {
    return {
      isValid: false,
      verified: false,
      error: 'Please enter your complete street address, city, state, and ZIP code.',
    };
  }

  const proxyClientId = getAddressClientId();
  if (isAddressProxyEnabled() && proxyClientId) {
    try {
      const proxyResult = await verifyAddressViaProxy(proxyClientId, candidateAddress);
      if (proxyResult?.verified === true && proxyResult.normalizedAddress) {
        return {
          isValid: true,
          verified: true,
          normalizedAddress: proxyResult.normalizedAddress,
        };
      }
      if (proxyResult?.verified === false) {
        return {
          isValid: false,
          verified: false,
          error:
            proxyResult.error
            || 'Address not recognized. Please check your street address, city, state, and ZIP code before continuing.',
        };
      }
    } catch (error) {
      console.warn('Address proxy verification failed, trying embedded Smarty:', error);
    }
  }

  const client = options.streetClient || await getSmartyStreetApiClient(options.smartyKey || SMARTY_EMBEDDED_KEY);
  if (!client) {
    if (options.allowUnverifiedFallback === false) {
      return {
        isValid: false,
        verified: false,
        error: 'Address verification is temporarily unavailable. Please verify your address details.',
      };
    }
    // If Smarty is not configured in the environment, allow complete address as fallback
    return {
      isValid: true,
      verified: true,
      normalizedAddress: {
        street: String(street || '').trim(),
        apt: String(apt || '').trim(),
        city: String(city || '').trim(),
        state: String(state || '').trim().toUpperCase(),
        zip: String(zip || '').trim(),
      },
    };
  }

  try {
    let lookup;
    if (typeof options.createLookup === 'function') {
      lookup = options.createLookup();
    } else {
      try {
        const SmartySDK = await import('smartystreets-javascript-sdk');
        lookup = new SmartySDK.usStreet.Lookup();
      } catch {
        lookup = {};
      }
    }
    lookup.street = String(street || '').trim();
    lookup.secondary = String(apt || '').trim();
    lookup.city = String(city || '').trim();
    lookup.state = String(state || '').trim();
    lookup.zipCode = String(zip || '').trim();
    lookup.match = 'enhanced';

    const response = await client.send(lookup);
    const matches = response.result || response.lookups?.[0]?.result || [];
    const match = matches[0];

    if (match && ['Y', 'S', 'D'].includes(match.analysis?.dpvMatchCode)) {
      const components = match.components || {};
      const normalizedAddress = {
        street: match.delivery_line_1 || String(street || '').trim(),
        apt: String(apt || components.secondary_number || '').trim(),
        city: components.city_name || String(city || '').trim(),
        state: (components.state_abbreviation || String(state || '')).trim().toUpperCase(),
        zip: components.zipcode || String(zip || '').trim(),
      };

      return {
        isValid: true,
        verified: true,
        normalizedAddress,
      };
    }

    return {
      isValid: false,
      verified: false,
      error: 'Address not recognized. Please check your street address, city, state, and ZIP code before continuing.',
    };
  } catch (error) {
    console.warn('Smarty address verification error:', error);
    return {
      isValid: false,
      verified: false,
      error: 'Address verification is temporarily unavailable. Please verify your address details.',
    };
  }
}
